import type { SurfaceTone } from './brandPalette'

export type SiteTemplateId =
  | 'aurora'
  | 'atelier'
  | 'horizonte'
  | 'prisma'
  | 'nobre'
  | 'brisa'
  | 'metropole'
  | 'essencial'
  | 'terra'
  | 'vitrine'
  | 'mosaico'
  | 'grade'
  | 'classico'
  | 'panorama'
  | 'studio'
  | 'lumen'
  | 'boulevard'
  | 'oasis'
  | 'ancora'
  | 'nordico'
  | 'concreto'
  | 'zen'
  | 'vertice'
  | 'jardim'
  | 'atrio'

export type SiteHeroLayout =
  | 'spotlight'
  | 'editorial'
  | 'cinema'
  | 'bento'
  | 'frame'
  | 'arch'
  | 'split'
  | 'minimal'
  | 'collage'
  | 'showcase'
  | 'mosaic'
  | 'swiss'
  | 'portal'
  | 'panorama'
  | 'marquee'
  | 'mesh'
  | 'cover'
  | 'oasis'
  | 'inset'

export type SiteCardLayout = 'soft' | 'editorial' | 'overlay' | 'luxe' | 'portal' | 'minimal' | 'bento' | 'swiss' | 'brutal' | 'showcase' | 'chip'

export interface SiteTemplate {
  id: SiteTemplateId
  name: string
  description: string
  tags: string[]
  fonts: string
  serif: boolean
  tone: SurfaceTone
  radius: number
  pill: boolean
  hero: SiteHeroLayout
  card: SiteCardLayout
  variant?: 'reverse' | 'slant' | 'center' | 'brand' | 'dark' | 'light'
}

export const SITE_TEMPLATES: SiteTemplate[] = [
  {
    id: 'aurora',
    name: 'Aurora',
    description: 'Moderno e luminoso: busca em destaque, cantos generosos e um brilho suave da sua cor.',
    tags: ['Moderno', 'Claro'],
    fonts: 'Plus Jakarta Sans',
    serif: false,
    tone: 'neutral',
    radius: 18,
    pill: true,
    hero: 'spotlight',
    card: 'soft'
  },
  {
    id: 'atelier',
    name: 'Atelier',
    description: 'Editorial e sofisticado, com serifa expressiva, fotos verticais e muito respiro.',
    tags: ['Elegante', 'Editorial'],
    fonts: 'Fraunces + Inter',
    serif: true,
    tone: 'warm',
    radius: 0,
    pill: false,
    hero: 'editorial',
    card: 'editorial'
  },
  {
    id: 'horizonte',
    name: 'Horizonte',
    description: 'Cinemático: foto em tela cheia, cabeçalho transparente e cards com texto sobre a imagem.',
    tags: ['Fotográfico', 'Imersivo'],
    fonts: 'Manrope',
    serif: false,
    tone: 'neutral',
    radius: 14,
    pill: false,
    hero: 'cinema',
    card: 'overlay'
  },
  {
    id: 'prisma',
    name: 'Prisma',
    description: 'Grade em blocos (bento) com números em destaque e uma pegada tecnológica.',
    tags: ['Moderno', 'Tecnológico'],
    fonts: 'Space Grotesk + Inter',
    serif: false,
    tone: 'cool',
    radius: 16,
    pill: false,
    hero: 'bento',
    card: 'bento'
  },
  {
    id: 'nobre',
    name: 'Nobre',
    description: 'Alto padrão clássico: serifa refinada, linhas finas, cantos retos e detalhes em caixa alta.',
    tags: ['Elegante', 'Luxo'],
    fonts: 'Cormorant Garamond + Manrope',
    serif: true,
    tone: 'warm',
    radius: 0,
    pill: false,
    hero: 'frame',
    card: 'luxe'
  },
  {
    id: 'brisa',
    name: 'Brisa',
    description: 'Leve e acolhedor, com formas orgânicas, arcos e superfícies tingidas pela sua cor.',
    tags: ['Acolhedor', 'Leve'],
    fonts: 'Outfit',
    serif: false,
    tone: 'tinted',
    radius: 26,
    pill: true,
    hero: 'arch',
    card: 'soft'
  },
  {
    id: 'metropole',
    name: 'Metrópole',
    description: 'Urbano e corporativo: painel na sua cor, barra de contatos e lista detalhada com mapa.',
    tags: ['Corporativo', 'Urbano'],
    fonts: 'Archivo + Inter',
    serif: false,
    tone: 'neutral',
    radius: 4,
    pill: false,
    hero: 'split',
    card: 'portal',
    variant: 'brand'
  },
  {
    id: 'essencial',
    name: 'Essencial',
    description: 'Minimalista: tipografia forte, nada de excessos e a cor aparece só onde importa.',
    tags: ['Minimalista', 'Direto'],
    fonts: 'Inter',
    serif: false,
    tone: 'pure',
    radius: 8,
    pill: false,
    hero: 'minimal',
    card: 'minimal'
  },
  {
    id: 'terra',
    name: 'Terra',
    description: 'Orgânico e caloroso: colagem com fotos dos seus imóveis e serifa amigável.',
    tags: ['Acolhedor', 'Orgânico'],
    fonts: 'Lora + Figtree',
    serif: true,
    tone: 'warm',
    radius: 20,
    pill: true,
    hero: 'collage',
    card: 'soft'
  },
  {
    id: 'vitrine',
    name: 'Vitrine',
    description: 'Seus imóveis são a capa: carrossel de destaques com preço e chamada para visita.',
    tags: ['Fotográfico', 'Comercial'],
    fonts: 'Sora',
    serif: false,
    tone: 'neutral',
    radius: 14,
    pill: false,
    hero: 'showcase',
    card: 'showcase'
  },
  {
    id: 'mosaico',
    name: 'Mosaico',
    description: 'Um mosaico de fotos dos imóveis abre o site, com cards fotográficos e marcantes.',
    tags: ['Fotográfico', 'Criativo'],
    fonts: 'Bricolage Grotesque',
    serif: false,
    tone: 'neutral',
    radius: 18,
    pill: false,
    hero: 'mosaic',
    card: 'overlay'
  },
  {
    id: 'grade',
    name: 'Grade',
    description: 'Estilo suíço: grid aparente, numeração, caixa alta e precisão tipográfica.',
    tags: ['Minimalista', 'Suíço'],
    fonts: 'Inter',
    serif: false,
    tone: 'pure',
    radius: 0,
    pill: false,
    hero: 'swiss',
    card: 'swiss'
  },
  {
    id: 'classico',
    name: 'Clássico',
    description: 'O portal imobiliário que todo cliente entende: busca completa sobre a foto e cards informativos.',
    tags: ['Corporativo', 'Tradicional'],
    fonts: 'Figtree',
    serif: false,
    tone: 'neutral',
    radius: 12,
    pill: false,
    hero: 'portal',
    card: 'portal'
  },
  {
    id: 'panorama',
    name: 'Panorama',
    description: 'Uma faixa panorâmica de foto, busca flutuante e cards com preço sobre a imagem.',
    tags: ['Moderno', 'Amplo'],
    fonts: 'Urbanist',
    serif: false,
    tone: 'neutral',
    radius: 22,
    pill: true,
    hero: 'panorama',
    card: 'showcase',
    variant: 'dark'
  },
  {
    id: 'studio',
    name: 'Studio',
    description: 'Ousado e criativo: título gigante, faixa de fotos em movimento e blocos na sua cor.',
    tags: ['Ousado', 'Criativo'],
    fonts: 'Syne + DM Sans',
    serif: false,
    tone: 'neutral',
    radius: 0,
    pill: true,
    hero: 'marquee',
    card: 'editorial'
  },
  {
    id: 'lumen',
    name: 'Lumen',
    description: 'Gradientes suaves derivados da sua cor, vidro fosco e uma atmosfera premium.',
    tags: ['Moderno', 'Premium'],
    fonts: 'Sora + Inter',
    serif: false,
    tone: 'tinted',
    radius: 22,
    pill: true,
    hero: 'mesh',
    card: 'bento'
  },
  {
    id: 'boulevard',
    name: 'Boulevard',
    description: 'Como a capa de uma revista: manchete em serifa, chamadas e fotografia em destaque.',
    tags: ['Elegante', 'Revista'],
    fonts: 'Playfair Display + Inter',
    serif: true,
    tone: 'warm',
    radius: 0,
    pill: true,
    hero: 'cover',
    card: 'editorial'
  },
  {
    id: 'oasis',
    name: 'Oásis',
    description: 'Clima de resort: foto ampla, cartão de busca central e serifa delicada.',
    tags: ['Fotográfico', 'Resort'],
    fonts: 'DM Serif Display + DM Sans',
    serif: true,
    tone: 'warm',
    radius: 18,
    pill: true,
    hero: 'oasis',
    card: 'overlay'
  },
  {
    id: 'ancora',
    name: 'Âncora',
    description: 'Sólido e confiável: números da empresa em evidência e informação bem organizada.',
    tags: ['Corporativo', 'Confiável'],
    fonts: 'IBM Plex Sans',
    serif: false,
    tone: 'cool',
    radius: 6,
    pill: false,
    hero: 'split',
    card: 'portal',
    variant: 'reverse'
  },
  {
    id: 'nordico',
    name: 'Nórdico',
    description: 'Escandinavo: tons neutros, muito branco, foto panorâmica e cantos suaves.',
    tags: ['Minimalista', 'Escandinavo'],
    fonts: 'Figtree',
    serif: false,
    tone: 'warm',
    radius: 12,
    pill: false,
    hero: 'panorama',
    card: 'minimal',
    variant: 'light'
  },
  {
    id: 'concreto',
    name: 'Concreto',
    description: 'Neobrutalista: bordas grossas, sombras sólidas e tipografia larga e marcante.',
    tags: ['Ousado', 'Brutalista'],
    fonts: 'Unbounded + Space Grotesk',
    serif: false,
    tone: 'pure',
    radius: 0,
    pill: false,
    hero: 'marquee',
    card: 'brutal',
    variant: 'brand'
  },
  {
    id: 'zen',
    name: 'Zen',
    description: 'Silencioso e contemplativo: serifa fina, espaços amplos e foco total nas fotos.',
    tags: ['Minimalista', 'Sereno'],
    fonts: 'Instrument Serif + Manrope',
    serif: true,
    tone: 'warm',
    radius: 0,
    pill: true,
    hero: 'minimal',
    card: 'minimal',
    variant: 'center'
  },
  {
    id: 'vertice',
    name: 'Vértice',
    description: 'Recorte diagonal entre a foto e o painel, com tipografia condensada e muita energia.',
    tags: ['Ousado', 'Dinâmico'],
    fonts: 'Archivo + Manrope',
    serif: false,
    tone: 'cool',
    radius: 10,
    pill: false,
    hero: 'split',
    card: 'showcase',
    variant: 'slant'
  },
  {
    id: 'jardim',
    name: 'Jardim',
    description: 'Delicado e natural: arcos, serifa elegante e superfícies suavemente tingidas.',
    tags: ['Acolhedor', 'Delicado'],
    fonts: 'Fraunces + Outfit',
    serif: true,
    tone: 'tinted',
    radius: 28,
    pill: true,
    hero: 'arch',
    card: 'soft',
    variant: 'reverse'
  },
  {
    id: 'atrio',
    name: 'Átrio',
    description: 'Foto emoldurada com canto recortado, títulos em caixa alta, vitrine de destaques e imóvel com galeria em mosaico.',
    tags: ['Moderno', 'Fotográfico'],
    fonts: 'Archivo + Figtree',
    serif: false,
    tone: 'pure',
    radius: 20,
    pill: true,
    hero: 'inset',
    card: 'chip'
  }
]

export const DEFAULT_SITE_TEMPLATE: SiteTemplateId = 'aurora'

const LEGACY_STYLES: Record<string, SiteTemplateId> = {
  'hero-1': 'aurora',
  'hero-2': 'metropole',
  'hero-3': 'horizonte',
  'hero-4': 'essencial',
  'hero-5': 'atelier',
  'hero-6': 'lumen',
  'hero-7': 'oasis',
  'hero-8': 'panorama',
  'hero-9': 'mosaico',
  'hero-10': 'nobre'
}

const BY_ID = new Map(SITE_TEMPLATES.map(template => [template.id, template]))

export function resolveSiteTemplate(style?: string | null): SiteTemplate {
  if (style && BY_ID.has(style as SiteTemplateId)) return BY_ID.get(style as SiteTemplateId)!
  if (style && LEGACY_STYLES[style]) return BY_ID.get(LEGACY_STYLES[style])!
  return BY_ID.get(DEFAULT_SITE_TEMPLATE)!
}

export const SITE_TEMPLATE_STYLES = ['Moderno', 'Elegante', 'Minimalista', 'Fotográfico', 'Ousado', 'Corporativo', 'Acolhedor'] as const

export type SiteTemplateStyle = (typeof SITE_TEMPLATE_STYLES)[number]

export function filterSiteTemplates(style?: string | null): SiteTemplate[] {
  if (!style) return SITE_TEMPLATES
  return SITE_TEMPLATES.filter(template => template.tags[0] === style)
}

export function hslToCssString({ h, s, l }: { h: number; s: number; l: number }): string {
  return `${h},${s},${l}`
}
